<template>
  <aside
    v-bind="$attrs"
    class="border-r border-tertiary bg-secondary overflow-y-auto"
    aria-label="Project navigation"
  >
    <!-- User Profile Header -->
    <div class="flex items-center justify-between bg-accent px-4 py-3 text-white shadow-md">
      <div class="flex items-center gap-2">
        <h2 class="text-lg font-bold tracking-wide">
          {{ username }}
        </h2>
      </div>
      <div v-if="!readOnly" class="flex gap-1">
        <button
          type="button"
          class="flex h-8 w-8 items-center justify-center rounded-standard bg-transparent text-white hover:bg-black/10 transition-colors"
          title="Alle einklappen"
          aria-label="Alle einklappen"
          @click="collapseSignal++"
        >
          <MdiIcon :path="mdiFormatVerticalAlignCenter" size="28" />
        </button>
        <button
          type="button"
          data-testid="sidebar-new"
          class="flex h-8 w-8 items-center justify-center rounded-standard bg-transparent text-white hover:bg-black/10 transition-colors"
          title="Neu hinzufügen"
          aria-label="Neu hinzufügen"
          @click="$emit('open-new-file')"
        >
          <MdiIcon :path="mdiPlusCircle" size="28" />
        </button>
      </div>
    </div>

    <!-- Project Tree -->
    <nav class="py-2 text-sm" aria-label="Project tree">
      <template v-if="root === null">
        <div class="px-4 py-8 text-center text-text-main/60">Lade…</div>
      </template>
      <template v-else>
        <SidebarTreeNode
          :folder="root"
          :depth="0"
          :active-file="activeFile"
          :collapse-signal="collapseSignal"
          @update:active-file="(path) => $emit('update:activeFile', path)"
          @open-new-file="$emit('open-new-file')"
          @rename-file="onSidebarRenameFile"
          @delete-file="onSidebarDeleteFile"
          @move-file="onSidebarMoveFile"
          @change-version="(projectId) => $emit('change-version', projectId)"
          @view-history="(path) => $emit('view-history', path)"
          @change-permissions="(projectId) => $emit('change-permissions', projectId)"
          @show-submissions="(path) => $emit('show-submissions', path)"
          @rename-folder="onSidebarRenameFolder"
          @delete-folder="onSidebarDeleteFolder"
          @rename-project="(id, name) => onSidebarRenameProject(id, name)"
          @share-project="(id) => $emit('share-project', id)"
          @delete-project="(id) => $emit('delete-project', id)"
        />
        <div v-if="examsRoot" class="mt-4 border-t border-tertiary pt-2">
          <SidebarTreeNode
            :folder="examsRoot"
            :depth="0"
            :active-file="activeFile"
            :collapse-signal="collapseSignal"
            @update:active-file="(path) => $emit('update:activeFile', path)"
            @open-new-file="$emit('open-new-file')"
            @rename-file="onSidebarRenameFile"
            @delete-file="onSidebarDeleteFile"
            @move-file="onSidebarMoveFile"
            @change-version="(path) => $emit('change-version', path)"
            @view-history="(path) => $emit('view-history', path)"
            @change-permissions="(path) => $emit('change-permissions', path)"
            @show-submissions="(path) => $emit('show-submissions', path)"
            @rename-folder="onSidebarRenameFolder"
            @delete-folder="onSidebarDeleteFolder"
            @share-project="(id) => $emit('share-project', id)"
            @delete-project="(id) => $emit('delete-project', id)"
          />
        </div>
      </template>
    </nav>
  </aside>

  <RenameFileOverlay
    :is-open="activeModal === 'rename'"
    :file="modalFilePath"
    @close="closeModal"
    @save="onRenameSave"
  />

  <MoveFileOverlay
    :is-open="activeModal === 'move'"
    :file="modalFilePath"
    :projects="mappedProjects"
    @close="closeModal"
    @save="onMoveSave"
  />

  <DeleteFileOverlay
    :is-open="activeModal === 'delete'"
    :file="modalFilePath"
    @close="closeModal"
    @confirm="onDeleteConfirm"
  />

  <RenameFileOverlay
    :is-open="folderModal === 'rename'"
    :file="modalFolderName"
    entity-label="Ordner"
    @close="closeFolderModal"
    @save="onFolderRenameSave"
  />

  <DeleteFileOverlay
    :is-open="folderModal === 'delete'"
    :file="modalFolderName"
    entity-label="Ordner"
    @close="closeFolderModal"
    @confirm="onFolderDeleteConfirm"
  />

  <RenameFileOverlay
    :is-open="projectModal === 'rename'"
    :file="modalProjectName"
    entity-label="Projekt"
    @close="closeProjectModal"
    @save="onProjectRenameSave"
  />
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { mdiFormatVerticalAlignCenter, mdiPlusCircle } from '@mdi/js'
import MdiIcon from '@/components/MdiIcon.vue'
import RenameFileOverlay from './overlays/RenameFileOverlay.vue'
import MoveFileOverlay from './overlays/MoveFileOverlay.vue'
import DeleteFileOverlay from './overlays/DeleteFileOverlay.vue'
import SidebarTreeNode from './SidebarTreeNode.vue'
import { TreeFolder } from '@/services/projectService'
import { uidPath } from '@/services/projectService'

const props = defineProps<{
  username: string
  root: TreeFolder | null
  examsRoot?: TreeFolder | null
  activeFile: string
  readOnly?: boolean
  demoMode?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:activeFile', uid: string): void
  (e: 'open-new-file'): void
  (e: 'open-new-exam'): void
  (e: 'rename-file', uid: string, newName: string): void
  (e: 'delete-file', uid: string): void
  (e: 'move-file', uid: string, targetFolderId: string): void
  (e: 'change-version', projectId: string): void
  (e: 'view-history', uid: string): void
  (e: 'change-permissions', projectId: string): void
  (e: 'show-submissions', uid: string): void
  (e: 'rename-folder', folderId: string, newName: string): void
  (e: 'delete-folder', folderId: string): void
  (e: 'rename-project', projectId: string, newName: string): void
  (e: 'share-project', projectId: string): void
  (e: 'delete-project', projectId: string): void
}>()

// Bumped by the header button to broadcast a "collapse all" to every node.
const collapseSignal = ref(0)

// Modal state (for overlays managed at sidebar level). The file is tracked by
// its uid (projectId + path) so the action always targets the right project's
// copy; the overlays only ever display its basename.
const activeModal = ref<'rename' | 'move' | 'delete' | null>(null)
const modalFileUid = ref('')
const modalFilePath = computed(() => uidPath(modalFileUid.value))

function openModal(type: 'rename' | 'move' | 'delete', uid: string) {
  activeModal.value = type
  modalFileUid.value = uid
}

function closeModal() {
  activeModal.value = null
}

// Folder modal state — the composite folder id is kept for the emit, while the
// overlays only ever display the folder's basename.
const folderModal = ref<'rename' | 'delete' | null>(null)
const modalFolderId = ref('')
const modalFolderName = computed(() => {
  const id = modalFolderId.value
  return id.includes('/') ? id.slice(id.lastIndexOf('/') + 1) : id
})

function closeFolderModal() {
  folderModal.value = null
}

function onSidebarRenameFolder(folderId: string) {
  modalFolderId.value = folderId
  folderModal.value = 'rename'
}

function onSidebarDeleteFolder(folderId: string) {
  modalFolderId.value = folderId
  folderModal.value = 'delete'
}

function onFolderRenameSave(newName: string) {
  emit('rename-folder', modalFolderId.value, newName)
}

function onFolderDeleteConfirm() {
  emit('delete-folder', modalFolderId.value)
}

// Project rename modal state — the project id is a bare UUID (no embedded
// name like composite folder ids), so the display name is captured separately
// when the overlay opens.
const projectModal = ref<'rename' | null>(null)
const modalProjectId = ref('')
const modalProjectName = ref('')

function closeProjectModal() {
  projectModal.value = null
}

function onSidebarRenameProject(projectId: string, name: string) {
  modalProjectId.value = projectId
  modalProjectName.value = name
  projectModal.value = 'rename'
}

function onProjectRenameSave(newName: string) {
  emit('rename-project', modalProjectId.value, newName)
}

// Overlay save/confirm handlers
function onRenameSave(newName: string) {
  emit('rename-file', modalFileUid.value, newName)
}

function onMoveSave(newFolderId: string) {
  emit('move-file', modalFileUid.value, newFolderId)
}

function onDeleteConfirm() {
  emit('delete-file', modalFileUid.value)
}

// The context menu emits rename/delete/move as "intent" signals (the user picked
// that menu item for this file). We intercept each here to open the matching
// overlay, and only emit the real action to the parent once the user confirms —
// so Delete always asks first and Move always has a chosen target.
function onSidebarRenameFile(uid: string) {
  // Im Demo-Modus kein Overlay öffnen — direkt das Event feuern, damit die
  // "deaktiviert"-Meldung erscheint (analog zu delete-file/move-file).
  if (props.demoMode) {
    emit('rename-file', uid, '')
    return
  }
  openModal('rename', uid)
}

function onSidebarDeleteFile(uid: string) {
  openModal('delete', uid)
}

function onSidebarMoveFile(uid: string) {
  openModal('move', uid)
}

// MoveFileOverlay expects Project[] shape with { id, name, folders: { id, name, files: string[] }[] }
// TreeFolder has { id, name, folders: TreeFolder[], files: TreeFile[] }
// Each project becomes an optgroup whose options are the project root itself plus
// every nested subfolder (indented by depth) — so a file can be moved into the
// project root even when the project has no subfolders.
function flattenFolders(
  folder: TreeFolder,
  depth = 0
): { id: string; name: string; files: string[] }[] {
  const indent = '  '.repeat(depth)
  const self = { id: folder.id, name: `${indent}${folder.name}`, files: [] as string[] }
  return [self, ...folder.folders.flatMap((sub) => flattenFolders(sub, depth + 1))]
}

const mappedProjects = computed(() => {
  if (!props.root) return []
  return props.root.folders.map((project) => ({
    id: project.id,
    name: project.name,
    folders: flattenFolders(project),
  }))
})
</script>
